import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import DatePicker, { DateObject } from 'react-multi-date-picker';
import { Add, DeleteSweep, EditCalendar, Remove, Schedule } from '@mui/icons-material';
import { clamp, filter, find, includes, isEmpty, isNil, join, map, round, sortBy, uniq } from 'lodash';
import dayjs from 'dayjs';
import { MobileTimePicker } from '@mui/x-date-pickers';
import FormActions from './form_actions';
import { CreateGuestJobValueType } from './page';
import { createScopedI18n } from '../../i18n/i18n';
import { hookFormNestedFieldErrorMessage, labelFieldEnum } from '../../utils/libs';
import { NumberTextField, RadioButtonGroup } from '../../components';

interface JobDetailFormProps {
  form: UseFormReturn<CreateGuestJobValueType>;
  onNext: () => void;
  onPrevious: () => void;
  loading?: boolean;
}

const jobTypeOptions = () => [
  {
    id: 'serve',
    duties: ['serving', 'set_table', 'clean_restaurant', 'clean_table'],
    defaultDuties: ['serving', 'clean_table'],
  },
  {
    id: 'wash',
    duties: ['wash_dish', 'clean_kitchen'],
    defaultDuties: ['wash_dish'],
  },
  {
    id: 'kitchen',
    duties: ['kitchen_food', 'kitchen_ingredient', 'kitchen_storing', 'kitchen_clean'],
    defaultDuties: ['kitchen_food', 'kitchen_storing'],
  },
  { id: 'other', duties: [], defaultDuties: [] },
];

const jobFieldI18n = createScopedI18n('graphql.fields.job');
const jobCreatePageI18n = createScopedI18n('pages.jobs_new');

const MAX_SCHEDULE_DAYS = 12;
const ADDTIONAL_END_TIME_HOURS = 5;

const JobDetailForm: React.FC<JobDetailFormProps> = ({ form, onNext, onPrevious, loading }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const datePickerRef = React.useRef<any>(null);

  const {
    control,
    setValue,
    getValues,
    watch,
    trigger,
    formState: { errors: validateErrors },
  } = form;
  const scheduleDates = watch('scheduleDates');
  const jobType = watch('jobType');
  const startTime = watch('startTime');
  const endTime = watch('endTime');
  const diffStartEndTime = round(dayjs(endTime).diff(dayjs(startTime), 'hour', true), 1);

  const maxNumberOfPosition: number = 20;
  const numberOfPositionHandler = (positionNumber: number | null) => {
    if (isNil(positionNumber)) {
      // @ts-expect-error Let user fix this by enter something to form.
      setValue('numberOfPosition', null);
      return;
    }

    let nextValue = positionNumber;
    if (!Number.isFinite(nextValue)) {
      return;
    }

    if (maxNumberOfPosition !== -1) {
      nextValue = clamp(nextValue, 0, maxNumberOfPosition);
    } else {
      nextValue = nextValue < 0 ? 0 : nextValue;
    }
    setValue('numberOfPosition', nextValue);
  };

  const jobTypeOptionWithOther = React.useMemo(() => jobTypeOptions(), [jobTypeOptions]);
  const jobDutyOptions = find(jobTypeOptionWithOther, (jobAndDuty) => jobAndDuty.id === jobType);

  const jobDutiesHandler = (checked: boolean, value: string) => {
    let newDuties = getValues('duties');
    if (checked) {
      newDuties = uniq([value, ...newDuties]);
    } else {
      newDuties = filter(newDuties, (duty) => duty !== value);
    }
    setValue('duties', newDuties, { shouldTouch: true });
  };

  const handleNext = async () => {
    const isValid = await trigger([
      'scheduleDates',
      'startTime',
      'endTime',
      'gender',
      'breakDurationMinute',
      'numberOfPosition',
      'jobType',
      'duties',
      'remark',
    ]);
    if (isValid) {
      onNext();
    }
  };

  const handlePrevious = () => {
    onPrevious();
  };

  // Set endTime/scheduleDateTimes when startTime changes
  useEffect(() => {
    if (!isEmpty(startTime)) {
      const startTimeMinute = dayjs(startTime).minute();
      const newEndTime = dayjs(startTime)
        .add(ADDTIONAL_END_TIME_HOURS, 'hour')
        .minute(startTimeMinute)
        .second(0)
        .millisecond(0)
        .toDate();
      setValue('endTime', newEndTime, { shouldTouch: true });
    }

    if (!isEmpty(scheduleDates) && !isEmpty(startTime)) {
      const scheduleDateTimes = map(scheduleDates, (date) => {
        const startAt = dayjs(startTime);
        const jobStartAt = dayjs(date).hour(startAt.hour()).minute(startAt.minute()).second(0).millisecond(0);
        const jobEndAt = jobStartAt.add(ADDTIONAL_END_TIME_HOURS, 'hour');

        return {
          jobStartAt: jobStartAt.toDate(),
          jobEndAt: jobEndAt.toDate(),
        };
      });
      setValue('scheduleDateTimes', scheduleDateTimes, { shouldTouch: true });
    }
  }, [scheduleDates, startTime, setValue]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Controller
        key="scheduleDates"
        name="scheduleDates"
        control={control}
        render={({ field: { name, onChange, onBlur, value }, fieldState: { invalid, error } }) => (
          <FormControl id={name} fullWidth error={invalid}>
            <Grid container item alignItems={'center'} gap={1}>
              <Grid item xs={3}>
                <FormLabel>{jobFieldI18n('schedule_dates')} *</FormLabel>
              </Grid>
              <Grid item xs="auto">
                <DatePicker
                  ref={datePickerRef}
                  multiple
                  name={name}
                  minDate={dayjs().startOf('day').toDate()}
                  maxDate={dayjs().endOf('day').add(MAX_SCHEDULE_DAYS, 'days').toDate()}
                  value={value}
                  onChange={(dates: DateObject[] | null) => {
                    onChange(
                      map(
                        sortBy(dates, (date) => date),
                        (date) => date.toDate(),
                      ),
                    );
                    onBlur();
                  }}
                  render={
                    <IconButton data-testid="schedule-dates-icon">
                      <EditCalendar />
                    </IconButton>
                  }
                />
              </Grid>
              <Grid item xs overflow={'auto'}>
                <Button
                  color="inherit"
                  fullWidth
                  size="small"
                  onClick={() => datePickerRef.current?.openCalendar?.()}
                  sx={{ justifyContent: 'flex-start', minHeight: 48 }}
                >
                  {isEmpty(scheduleDates) ? (
                    <Typography variant="subtitle2" textAlign={'start'} sx={{ opacity: 0.6 }}>
                      {jobCreatePageI18n('schedule_date_hint', { max_date: MAX_SCHEDULE_DAYS })}
                    </Typography>
                  ) : (
                    <Stack direction={'row'} gap={1}>
                      {map(scheduleDates, (date, index) => (
                        <Box
                          key={index}
                          width={40}
                          height={40}
                          borderRadius={40}
                          flexShrink={0}
                          display={'inline-flex'}
                          justifyContent={'center'}
                          alignItems={'center'}
                          color={'primary.contrastText'}
                          bgcolor={'primary.main'}
                        >
                          <Typography>{dayjs(date).format('DD')}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Button>
              </Grid>

              <Collapse in={!isEmpty(scheduleDates)} orientation="horizontal">
                <IconButton onClick={() => onChange([])}>
                  <DeleteSweep />
                </IconButton>
              </Collapse>
            </Grid>
            <Grid container item gap={1} justifyContent={'flex-end'}>
              <Grid item xs={9}>
                <FormHelperText>{hookFormNestedFieldErrorMessage(error, { joinErrorMessages: false })}</FormHelperText>
              </Grid>
            </Grid>
          </FormControl>
        )}
      />

      <Grid container item gap={1}>
        <Grid item xs={3}>
          <Stack minHeight={56} flexDirection={'row'} alignItems={'center'}>
            <FormLabel error={!isEmpty(validateErrors.startTime) || !isEmpty(validateErrors.endTime)}>
              {jobCreatePageI18n('job_time')} *
            </FormLabel>
          </Stack>
        </Grid>
        <Grid item container xs>
          <Controller
            key="startTime"
            name="startTime"
            control={control}
            render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
              <FormControl id={field.name} fullWidth error={invalid}>
                <Grid container item alignItems={'center'} gap={1}>
                  <Grid item xs="auto">
                    <FormLabel>{jobCreatePageI18n('job_time_from')}</FormLabel>
                  </Grid>
                  <Grid item xs>
                    <MobileTimePicker
                      ampm={false}
                      closeOnSelect
                      minutesStep={30}
                      value={value ?? null}
                      slotProps={{ textField: { InputProps: { endAdornment: <Schedule /> } } }}
                      {...field}
                    />
                  </Grid>
                </Grid>
                <Grid container item gap={1} justifyContent={'flex-end'}>
                  <FormHelperText>{error?.message}</FormHelperText>
                </Grid>
              </FormControl>
            )}
          />
        </Grid>
        <Grid item container xs>
          {endTime && (
            <Grid item xs="auto" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary">
                ถึง {dayjs(endTime).format('HH:mm')} ({diffStartEndTime} ชั่วโมง)
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>

      <Controller
        key="gender"
        name="gender"
        control={control}
        render={({ field: { name, onBlur, onChange, value }, fieldState: { invalid, error } }) => (
          <FormControl id={name} fullWidth error={invalid}>
            <Grid container item alignItems={'center'} gap={1}>
              <Grid item xs={3}>
                <FormLabel>{jobFieldI18n('gender')} *</FormLabel>
              </Grid>
              <Grid item xs overflow={'auto'} whiteSpace={'nowrap'}>
                <RadioButtonGroup
                  id={name}
                  color="primary"
                  value={value}
                  onChangeValue={onChange}
                  onBlur={onBlur}
                  items={[
                    { label: labelFieldEnum('job.genders', 'male'), value: 'male' },
                    { label: labelFieldEnum('job.genders', 'female'), value: 'female' },
                    { label: labelFieldEnum('job.genders', 'unspecified'), value: 'SET_NULL_ON_SUBMIT' },
                  ]}
                />
              </Grid>
            </Grid>
            <Grid container item gap={1} justifyContent={'flex-end'}>
              <Grid item xs={9}>
                <FormHelperText>{error?.message}</FormHelperText>
              </Grid>
            </Grid>
          </FormControl>
        )}
      />

      <Controller
        key="numberOfPosition"
        name="numberOfPosition"
        control={control}
        render={({
          field: { value: numberOfPosition, onChange: _onChange, ...field },
          fieldState: { invalid, error },
        }) => (
          <FormControl id={field.name} fullWidth error={invalid}>
            <Grid container item alignItems={'center'} gap={1}>
              <Grid item xs={3}>
                <FormLabel>{jobFieldI18n('number_of_position')} *</FormLabel>
              </Grid>
              <Grid item xs>
                <NumberTextField
                  {...field}
                  id={field.name}
                  inputMode="numeric"
                  value={numberOfPosition.toString()}
                  onNumberChange={numberOfPositionHandler}
                  error={invalid}
                  InputProps={{
                    inputProps: { pattern: '[0-9]*', sx: { maxWidth: 64, textAlign: 'center' } },
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          disabled={(numberOfPosition ?? 0) <= 1}
                          onClick={() => numberOfPositionHandler((numberOfPosition ?? 0) - 1)}
                          data-testid="number-of-position-decrement"
                        >
                          <Remove />
                        </IconButton>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          disabled={maxNumberOfPosition !== -1 && (numberOfPosition ?? 0) >= maxNumberOfPosition}
                          onClick={() => numberOfPositionHandler((numberOfPosition ?? 0) + 1)}
                          data-testid="number-of-position-increment"
                        >
                          <Add />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            <Grid container item gap={1} justifyContent={'flex-end'}>
              <Grid item xs={9}>
                <FormHelperText>{error?.message}</FormHelperText>
              </Grid>
            </Grid>
          </FormControl>
        )}
      />

      <Controller
        key="jobType"
        name="jobType"
        control={control}
        render={({ field: { name, onBlur, onChange, value }, fieldState: { invalid, error } }) => (
          <FormControl id={name} fullWidth error={invalid}>
            <Grid container item alignItems={'center'} gap={1}>
              <Grid item xs={3}>
                <FormLabel>{jobFieldI18n('job_type')} *</FormLabel>
              </Grid>
              <Grid item xs overflow={'auto'} whiteSpace={'nowrap'}>
                <RadioButtonGroup
                  id={name}
                  color="primary"
                  value={value}
                  onChangeValue={onChange}
                  onBlur={onBlur}
                  items={map(jobTypeOptionWithOther, (jobAndDuty) => ({
                    label: labelFieldEnum('job.job_types', jobAndDuty.id),
                    value: jobAndDuty.id,
                  }))}
                />
              </Grid>
            </Grid>
            <Grid container item gap={1} justifyContent={'flex-end'}>
              <Grid item xs={9}>
                <FormHelperText>{error?.message}</FormHelperText>
              </Grid>
            </Grid>
          </FormControl>
        )}
      />

      <Collapse key="duties" in={!isNil(jobType) && jobType !== 'other'} sx={{ width: '100%' }}>
        <Controller
          key="duties"
          name="duties"
          control={control}
          render={({ field: { name, value: duties }, fieldState: { invalid, error } }) => (
            <FormControl id={name} fullWidth error={invalid}>
              <Grid container item gap={1}>
                <Grid item xs={12}>
                  <FormLabel>{jobFieldI18n('duty')} *</FormLabel>
                </Grid>
                <Grid item xs={12}>
                  <FormGroup row>
                    {map(jobDutyOptions?.duties, (duty) => (
                      <Grid key={duty} item xs={12} sm={6}>
                        <FormControlLabel
                          label={labelFieldEnum('job.duties', duty)}
                          value={duty}
                          control={
                            <Checkbox
                              checked={includes(duties, duty)}
                              onChange={({ target: { checked } }) => jobDutiesHandler(checked, duty)}
                            />
                          }
                          sx={{ paddingLeft: 2 }}
                        />
                      </Grid>
                    ))}
                  </FormGroup>
                </Grid>
                <Grid container item>
                  <FormHelperText>{error?.message}</FormHelperText>
                </Grid>
              </Grid>
            </FormControl>
          )}
        />
      </Collapse>

      <Collapse key="remark" in={isNil(jobType) || jobType === 'other'} sx={{ width: '100%' }}>
        <Controller
          key="remark"
          name="remark"
          control={control}
          render={({ field, fieldState: { invalid, error } }) => (
            <FormControl id={field.name} fullWidth error={invalid}>
              <Grid container item gap={1}>
                <FormLabel>{jobFieldI18n('remark')} *</FormLabel>
                <TextField
                  id={field.name}
                  type="text"
                  fullWidth
                  multiline
                  minRows={4}
                  maxRows={8}
                  error={invalid}
                  placeholder={join(jobCreatePageI18n('remark_hint'))}
                  helperText={error?.message}
                  {...field}
                />
              </Grid>
            </FormControl>
          )}
        />
      </Collapse>

      <FormActions loading={loading} onNext={handleNext} onPrevious={handlePrevious} />
    </Box>
  );
};

export default JobDetailForm;
